import React, { useEffect, useMemo, useState } from "react";
import HelpBubble from "./HelpBubble";
import { BREAKEVEN_HELP } from "./helpTexts";
import { formatApiError, money, parseDecimalInput, pct, previewFormula } from "./pricingFormat";

const PERCENT_FIELDS = [
    ["commission_percent", "Комиссия площадки", "% от цены продажи"],
    ["payment_percent", "Платёжный сервис", "эквайринг; 0, если входит в комиссию"],
    ["other_percent", "Прочие", "резерв на возвраты и т. п."],
];
const BAND_FIELDS = [
    ["band_add_low_uah", "LOW"],
    ["band_add_mid_uah", "MID"],
    ["band_add_high_uah", "HIGH"],
    ["band_add_premium_uah", "PREMIUM"],
];
const NUMBER_FIELDS = [
    ...PERCENT_FIELDS.map(([key]) => key),
    "fixed_uah_per_item",
    ...BAND_FIELDS.map(([key]) => key),
    "default_supplier_profit_uah",
];

const toDraft = (settings) => {
    const draft = { pricing_mode: settings?.pricing_mode || "legacy", notes: settings?.notes || "" };
    NUMBER_FIELDS.forEach((key) => {
        const value = settings?.[key];
        draft[key] = value === null || value === undefined ? "" : String(Number(value));
    });
    return draft;
};

export const buildSettingsPayload = (draft) => {
    const payload = { pricing_mode: draft.pricing_mode, notes: draft.notes?.trim() || null };
    NUMBER_FIELDS.forEach((key) => {
        const raw = String(draft[key] ?? "").trim();
        const number = raw === "" ? 0 : parseDecimalInput(raw);
        if (Number.isNaN(number) || number < 0) {
            throw new Error("Все значения должны быть неотрицательными числами");
        }
        payload[key] = number;
    });
    const total = payload.commission_percent + payload.payment_percent + payload.other_percent;
    if (total >= 100) {
        throw new Error("Сумма процентов должна быть меньше 100");
    }
    if (payload.pricing_mode === "breakeven" && payload.commission_percent <= 0) {
        throw new Error("Для режима «от безубыточности» укажите комиссию площадки");
    }
    return payload;
};

function NumberField({ id, label, hint, unit, value, onChange, placeholder, disabled }) {
    return (
        <label className="mp-field" htmlFor={id}>
            {label}
            <div className="mp-input-suffix">
                <input
                    id={id}
                    className="mp-input"
                    type="text"
                    inputMode="decimal"
                    value={value}
                    placeholder={placeholder}
                    disabled={disabled}
                    onChange={(event) => onChange(event.target.value)}
                />
                <span>{unit}</span>
            </div>
            {hint ? <span className="mp-field-hint">{hint}</span> : null}
        </label>
    );
}

export default function BreakevenSettingsForm({ settings, platformLabel, saving, onSave }) {
    const [draft, setDraft] = useState(() => toDraft(settings));
    const [dirty, setDirty] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    useEffect(() => {
        setDraft(toDraft(settings));
        setDirty(false);
    }, [settings]);

    const change = (key, value) => {
        setDraft((old) => ({ ...old, [key]: value }));
        setDirty(true);
        setError("");
        setSuccess("");
    };

    const totalPercent = useMemo(
        () => PERCENT_FIELDS.reduce((sum, [key]) => sum + (parseDecimalInput(draft[key] || 0) || 0), 0),
        [draft],
    );
    const example = previewFormula(draft);

    const changeMode = (mode) => {
        if (mode === draft.pricing_mode) return;
        if (mode === "breakeven") {
            const confirmed = window.confirm(
                "Перевести аккаунт на цены «от безубыточности»? После сохранения цены в фиде пересчитаются по новой формуле.\n\n"
                + "Перед включением сравните старые и новые цены на сервере:\n"
                + (settings?.compare_cli_hint || ""),
            );
            if (!confirmed) return;
        }
        change("pricing_mode", mode);
    };

    const submit = async (event) => {
        event.preventDefault();
        let payload;
        try {
            payload = buildSettingsPayload(draft);
        } catch (validationError) {
            setError(validationError.message);
            return;
        }
        try {
            await onSave(payload);
            setDirty(false);
            setSuccess("Профиль сохранён.");
        } catch (apiError) {
            setError(formatApiError(apiError, "Не удалось сохранить профиль."));
        }
    };

    const defaultCommission = settings?.platform_default_commission_percent;

    return (
        <form className="mp-card" onSubmit={submit} aria-label="Профиль безубыточности">
            <HelpBubble title="Что такое безубыточность">{BREAKEVEN_HELP}</HelpBubble>
            <div className="mp-card-head">
                <div>
                    <h3 className="mp-title">Безубыточность {platformLabel}</h3>
                    <p className="mp-lead">
                        Затраты площадки без прибыли. Проценты берутся от цены продажи, гривны — с каждой единицы.
                        Прибыль задаётся отдельно: общая сумма ниже или своя у отправителя на вкладке отправителей.
                    </p>
                </div>
                {!settings?.exists ? <span className="mp-pill mp-pill-warn">Профиль ещё не сохранён</span> : null}
            </div>

            <div className="mp-groups">
                <div className="mp-group">
                    <h4 className="mp-group-title">Проценты от цены продажи</h4>
                    {PERCENT_FIELDS.map(([key, label, hint]) => (
                        <NumberField
                            key={key}
                            id={`mp-${key}`}
                            label={label}
                            hint={key === "commission_percent" && defaultCommission ? `${hint}; обычно ${pct(defaultCommission)}` : hint}
                            unit="%"
                            value={draft[key]}
                            disabled={saving}
                            onChange={(value) => change(key, value)}
                        />
                    ))}
                    <div className={totalPercent >= 100 ? "mp-callout mp-callout-error" : "mp-callout"}>
                        Итого от цены: <strong>{pct(totalPercent)}</strong>
                        {totalPercent >= 100 ? " — должно быть меньше 100 %" : ""}
                    </div>
                </div>

                <div className="mp-group">
                    <h4 className="mp-group-title">Гривны за единицу товара</h4>
                    <NumberField
                        id="mp-fixed_uah_per_item"
                        label="Фикс площадки на товар"
                        hint="упаковка, обработка, доставка до площадки"
                        unit="грн"
                        value={draft.fixed_uah_per_item}
                        disabled={saving}
                        onChange={(value) => change("fixed_uah_per_item", value)}
                    />
                    <h4 className="mp-group-title">Прочие затраты по диапазонам закупки</h4>
                    <div className="mp-grid-2">
                        {BAND_FIELDS.map(([key, label]) => (
                            <NumberField
                                key={key}
                                id={`mp-${key}`}
                                label={label}
                                unit="грн"
                                value={draft[key]}
                                placeholder="0"
                                disabled={saving}
                                onChange={(value) => change(key, value)}
                            />
                        ))}
                    </div>
                    <span className="mp-field-hint">Диапазоны те же, что в ценообразовании Tabletki. Можно оставить нулями.</span>
                </div>

                <div className="mp-group">
                    <h4 className="mp-group-title">Прибыль по умолчанию</h4>
                    <NumberField
                        id="mp-default_supplier_profit_uah"
                        label="Прибыль на единицу для всех отправителей"
                        hint="чистыми после комиссий; у отправителя можно задать свою"
                        unit="грн"
                        value={draft.default_supplier_profit_uah}
                        disabled={saving}
                        onChange={(value) => change("default_supplier_profit_uah", value)}
                    />
                    {settings?.supplier_profit_overrides?.length ? (
                        <p className="mp-muted">
                            Своя прибыль:{" "}
                            {settings.supplier_profit_overrides
                                .map((row) => `${row.supplier_name || row.supplier_code} — ${money(row.profit_uah)} грн`)
                                .join(", ")}
                        </p>
                    ) : (
                        <p className="mp-muted">Индивидуальной прибыли у отправителей нет.</p>
                    )}
                    {example ? (
                        <div className="mp-example" aria-label="Пример расчёта">
                            Пример для закупки {money(example.cost)} грн: нужно получить{" "}
                            <strong>{money(example.required)}</strong>, безубыточность{" "}
                            <strong>{money(example.breakeven)}</strong>, цена <strong>{money(example.price)}</strong>.
                        </div>
                    ) : null}
                </div>
            </div>

            <div className="mp-group">
                <h4 className="mp-group-title">Режим цены на аккаунте</h4>
                <div className="mp-radio-row" role="radiogroup" aria-label="Режим цены">
                    {[
                        ["legacy", "Старая цена", "цена магазина для площадок + доп. наценка"],
                        ["breakeven", "От безубыточности", "формула этого профиля"],
                    ].map(([mode, label, hint]) => (
                        <label key={mode} className={draft.pricing_mode === mode ? "mp-radio selected" : "mp-radio"}>
                            <input
                                type="radio"
                                name="pricing_mode"
                                value={mode}
                                checked={draft.pricing_mode === mode}
                                disabled={saving}
                                onChange={() => changeMode(mode)}
                            />
                            <span>
                                <strong>{label}</strong> <span className="mp-muted">— {hint}</span>
                            </span>
                        </label>
                    ))}
                </div>
                <span className="mp-field-hint">
                    Доп. наценка отправителя остаётся сверху и в новом режиме. Проверьте, что она не дублирует комиссию площадки.
                </span>
            </div>

            {error ? <div className="mp-callout mp-callout-error" role="alert">{error}</div> : null}
            {success ? <div className="mp-callout mp-callout-ok" role="status">{success}</div> : null}
            <div className="mp-actions">
                <button type="submit" className="mp-btn mp-btn-primary" disabled={saving || (!dirty && settings?.exists)}>
                    {saving ? "Сохраняем…" : "Сохранить профиль"}
                </button>
                {dirty ? <span className="mp-muted">Есть несохранённые изменения</span> : null}
            </div>
        </form>
    );
}
